/**
 * The Calendar section's pure half (`components/macro/CalendarSection.tsx`;
 * plan `docs/plans/macro.md`, mock `docs/mockups/macro-v2.html` section 03).
 *
 * Dates are `YYYY-MM-DD` strings throughout and arithmetic is done in UTC, so
 * a day never shifts with the viewer's zone. Weeks are Monday–Friday: the
 * calendar is of trading days, as the mock draws it.
 */
import type { CalendarSectionConfig, Layout } from "@qufolio/model";
import { chartSections } from "./active-section";
import type { EarningsDay, EarningsRow, EarningsSourceWire, EconEvent } from "../api/macro-calendar";

export type CalendarSource = NonNullable<CalendarSectionConfig["earningsSources"]>[number];

/** Rank order, and the chips' order (owner, macro.md: Journal › My lists › Group › Top cap). */
export const CALENDAR_SOURCES: readonly CalendarSource[] = ["journal", "lists", "group", "topCap"];

export const SOURCE_LABEL: Record<CalendarSource, { icon: string; label: string }> = {
  journal: { icon: "📓", label: "Journal" },
  lists: { icon: "★", label: "My lists" },
  group: { icon: "👥", label: "Group" },
  topCap: { icon: "🏛", label: "Top cap" },
};

const WIRE: Record<CalendarSource, EarningsSourceWire> = {
  journal: "journal",
  lists: "lists",
  group: "group",
  topCap: "topcap",
};
const FROM_WIRE: Record<EarningsSourceWire, CalendarSource> = {
  journal: "journal",
  lists: "lists",
  group: "group",
  topcap: "topCap",
};

export const toWireSource = (s: CalendarSource): EarningsSourceWire => WIRE[s];
export const fromWireSource = (s: EarningsSourceWire | "find"): CalendarSource | undefined =>
  s === "find" ? undefined : FROM_WIRE[s];

/** Every stored choice filled in, except the jump layout: absent there means "ask". */
export type ResolvedCalendarConfig = Required<Omit<CalendarSectionConfig, "jumpLayoutId">> &
  Pick<CalendarSectionConfig, "jumpLayoutId">;

/** Every absent field filled with the mock's default (the schema's descriptions). */
export function resolveCalendarConfig(c: CalendarSectionConfig | undefined): ResolvedCalendarConfig {
  return {
    range: c?.range ?? "week",
    layout: c?.layout ?? "list",
    view: c?.view ?? "merged",
    earningsSources: c?.earningsSources ?? [...CALENDAR_SOURCES],
    topCap: c?.topCap ?? 100,
    perDay: c?.perDay ?? 8,
    group: c?.group ?? "none",
    jumpLayoutId: c?.jumpLayoutId,
  };
}

/** Toggle a source, keeping rank order. */
export function toggleSource(list: readonly CalendarSource[], s: CalendarSource): CalendarSource[] {
  const on = new Set(list);
  if (on.has(s)) on.delete(s);
  else on.add(s);
  return CALENDAR_SOURCES.filter((x) => on.has(x));
}

// ── dates ────────────────────────────────────────────────────────────────

const DAY_MS = 86_400_000;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parse = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));
const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function addDays(iso: string, n: number): string {
  return fmt(parse(iso) + n * DAY_MS);
}

/** 0 = Sunday. */
export const weekday = (iso: string) => new Date(parse(iso)).getUTCDay();

/** The Monday of `iso`'s week; a Saturday or Sunday belongs to the week ahead. */
export function weekStart(iso: string): string {
  const d = weekday(iso);
  return addDays(iso, d === 0 ? 1 : d === 6 ? 2 : 1 - d);
}

/** Monday to Friday from a Monday. */
export function weekDays(monday: string): string[] {
  return [0, 1, 2, 3, 4].map((k) => addDays(monday, k));
}

export const monthOf = (iso: string) => iso.slice(0, 7);

/** The Mondays of every week holding a weekday of `iso`'s month. */
export function monthWeeks(iso: string): string[] {
  const month = monthOf(iso);
  const out: string[] = [];
  for (let m = weekStart(`${month}-01`); weekDays(m).some((d) => monthOf(d) === month); m = addDays(m, 7)) {
    out.push(m);
  }
  return out;
}

/** The month after (`+1`) or before (`-1`) `iso`'s, as its first day. */
export function shiftMonth(iso: string, n: number): string {
  const d = new Date(parse(`${monthOf(iso)}-01`));
  d.setUTCMonth(d.getUTCMonth() + n);
  return fmt(d.getTime());
}

/** Today in New York, `YYYY-MM-DD`, and the minutes since midnight there. */
export function nyNow(now: Date): { date: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, minutes: +get("hour") * 60 + +get("minute") };
}

export function dayLabel(iso: string): string {
  const d = new Date(parse(iso));
  return `${WEEKDAY[d.getUTCDay()]}, ${MON[d.getUTCMonth()]}. ${d.getUTCDate()}`;
}

export function shortDayLabel(iso: string): { weekday: string; date: string } {
  const d = new Date(parse(iso));
  return { weekday: WEEKDAY_SHORT[d.getUTCDay()]!, date: `${MON[d.getUTCMonth()]} ${d.getUTCDate()}` };
}

export function weekLabel(monday: string): string {
  const a = new Date(parse(monday));
  const b = new Date(parse(addDays(monday, 4)));
  const tail = a.getUTCMonth() !== b.getUTCMonth() ? `${MON[b.getUTCMonth()]} ${b.getUTCDate()}` : `${b.getUTCDate()}`;
  return `${MON[a.getUTCMonth()]} ${a.getUTCDate()} – ${tail}`;
}

export function monthLabel(iso: string): string {
  return `${MONTH_LONG[+iso.slice(5, 7) - 1]} ${iso.slice(0, 4)}`;
}

export const dayOfMonth = (iso: string) => +iso.slice(8, 10);

/** The days a view draws, and the span to fetch for it. */
export function viewDays(range: "week" | "month", anchor: string): { days: string[]; from: string; to: string } {
  if (range === "week") {
    const days = weekDays(weekStart(anchor));
    return { days, from: days[0]!, to: days[4]! };
  }
  const weeks = monthWeeks(anchor);
  const days = weeks.flatMap(weekDays);
  return { days, from: days[0]!, to: days[days.length - 1]! };
}

// ── times ────────────────────────────────────────────────────────────────

export type CalendarTz = "ET" | "PT";

/** `HH:MM` (24h, Eastern) → minutes; TBA sorts first, as the mock's `mins` does. */
export function minutesOf(t: string | null): number {
  if (!t) return -1;
  const [h, m] = t.split(":");
  return +h! * 60 + +m!;
}

/**
 * `08:30` → `8:30 AM` in Eastern, or `5:30 AM` in Pacific. Pacific is three
 * hours behind Eastern all year — both zones change on the same dates.
 */
export function formatTime(t: string | null, tz: CalendarTz): string {
  if (!t) return "TBA";
  let mins = minutesOf(t) - (tz === "PT" ? 180 : 0);
  if (mins < 0) mins += 1440;
  const h = Math.floor(mins / 60);
  const m = String(mins % 60).padStart(2, "0");
  return `${h % 12 || 12}:${m} ${h >= 12 ? "PM" : "AM"}`;
}

// ── economic ─────────────────────────────────────────────────────────────

export function econForDay(events: readonly EconEvent[], iso: string, keyOnly: boolean): EconEvent[] {
  return events
    .filter((e) => e.date === iso && (!keyOnly || e.key))
    .sort((a, b) => minutesOf(a.time) - minutesOf(b.time));
}

const num = (s: string | null) => (s == null ? NaN : parseFloat(s.replace(/[%KMB,]/g, "")));

/** `pos` above the forecast, `neg` below, `` when either is missing or they tie. */
export function surpriseClass(actual: string | null, forecast: string | null): "pos" | "neg" | "" {
  const d = num(actual) - num(forecast);
  if (Number.isNaN(d) || d === 0) return "";
  return d > 0 ? "pos" : "neg";
}

export function epsClass(actual: number | null, est: number | null): "pos" | "neg" | "" {
  if (actual == null || est == null || actual === est) return "";
  return actual > est ? "pos" : "neg";
}

/** Above this many earnings tiles a week-grid day lays them in two columns. */
export const TWO_COLUMNS_ABOVE = 10;

/** 1 or 2: how many tile columns a week-grid day gets, and its share of the width. */
export function dayColumns(tiles: number): 1 | 2 {
  return tiles > TWO_COLUMNS_ABOVE ? 2 : 1;
}

/**
 * The estimate against last quarter's reported EPS (owner, 2026-11-01): is the
 * street expecting more or less than the company just did?
 *
 * A percentage only when last quarter was positive — off a loss or zero a ratio
 * reads backwards (−0.10 → +0.05 is "−150%"), so there the change is shown as a
 * signed EPS difference instead. Colour follows the direction of the estimate.
 */
export function estVsLast(
  est: number | null,
  last: number | null,
): { text: string; cls: "pos" | "neg" | "" } | null {
  if (est == null || last == null || !Number.isFinite(est) || !Number.isFinite(last)) return null;
  const cls = est > last ? "pos" : est < last ? "neg" : "";
  if (last > 0) {
    const pct = (est / last - 1) * 100;
    const r = Math.round(pct);
    return { text: r === 0 ? "0%" : `${r > 0 ? "+" : "−"}${Math.abs(r)}%`, cls };
  }
  const d = est - last;
  return { text: d === 0 ? "0.00" : `${d > 0 ? "+" : "−"}${Math.abs(d).toFixed(2)}`, cls };
}

/** The next key release after `now` (New York), for the "Next:" pill. */
export function nextKeyEvent(
  events: readonly EconEvent[],
  now: { date: string; minutes: number },
): EconEvent | undefined {
  return events
    .filter((e) => e.key && (e.date > now.date || (e.date === now.date && e.time != null && minutesOf(e.time) > now.minutes)))
    .sort((a, b) => (a.date === b.date ? minutesOf(a.time) - minutesOf(b.time) : a.date < b.date ? -1 : 1))[0];
}

// ── earnings ─────────────────────────────────────────────────────────────

export interface DaySlice {
  show: EarningsRow[];
  /** Every reporter that day not shown, matched or not. */
  hidden: number;
  /** The matched part of `hidden`. */
  matchedHidden: number;
}

/**
 * A day's reporters cut to the section's per-day count. The server sends every
 * matched one ranked (`perDay=all`), so this is a slice; `perDay` 0 = all
 * matched, and an expanded day shows them all too.
 */
export function sliceDay(day: EarningsDay | undefined, perDay: number, expanded: boolean, limit?: number): DaySlice {
  if (!day) return { show: [], hidden: 0, matchedHidden: 0 };
  const n = expanded || perDay === 0 ? day.rows.length : Math.min(limit ?? perDay, day.rows.length);
  const show = day.rows.slice(0, n);
  return { show, hidden: day.total - show.length, matchedHidden: day.rows.length - show.length };
}

/** `$3.9T`, `$210B`, `$850M`. */
export function formatCap(dollars: number | null): string {
  if (dollars == null) return "";
  if (dollars >= 1e12) return `$${(dollars / 1e12).toFixed(1)}T`;
  if (dollars >= 1e9) return `$${Math.round(dollars / 1e9)}B`;
  return `$${Math.round(dollars / 1e6)}M`;
}

export const formatEps = (v: number | null) => (v == null ? "-" : v.toFixed(2));

/** Each row's "why shown" chips, from the wire sources. */
export function rowSources(row: EarningsRow): CalendarSource[] {
  return row.sources.map(fromWireSource).filter((s): s is CalendarSource => s !== undefined);
}

// ── earnings by sector ───────────────────────────────────────────────────

/** Names shown per sector, in each time block, with Group: Sector (owner, 2026-11-01). */
export const SECTOR_CAP = 3;
/** Sectors named in a day's "+N more" breakdown before "…". */
export const HIDDEN_SECTORS_NAMED = 4;
/** The heading for a row with no sector. */
export const OTHER_SECTOR = "Other";

/** A day's three time blocks, in the order the day draws them. */
export type TimeBlock = "bmo" | "amc" | "none";
export const TIME_BLOCKS: readonly TimeBlock[] = ["bmo", "amc", "none"];
export const blockOf = (r: EarningsRow): TimeBlock => r.time ?? "none";

/** FMP's sector names, shortened for the "+N more" line only; headers use the full name. */
const SECTOR_SHORT: Record<string, string> = {
  Technology: "Tech",
  "Financial Services": "Financials",
  "Communication Services": "Comm",
  "Consumer Cyclical": "Cons Cyc",
  "Consumer Defensive": "Cons Def",
  "Basic Materials": "Materials",
  Healthcare: "Health",
};
export const sectorShort = (label: string) => SECTOR_SHORT[label] ?? label;

export interface SectorGroup {
  /** The row's own sector; null is "Other". */
  sector: string | null;
  label: string;
  /** Every matched name of this sector in the block, shown or not. */
  total: number;
  /** The ones shown, in ranking order. */
  rows: EarningsRow[];
}

/**
 * Rows under sector headers: most names first, ties alphabetical, no sector
 * ("Other") last; within a sector the ranking order is kept. `cap` per sector,
 * `undefined` for all of them.
 */
export function groupBySector(rows: readonly EarningsRow[], cap?: number): SectorGroup[] {
  const by = new Map<string | null, EarningsRow[]>();
  for (const r of rows) {
    const k = r.sector || null;
    const list = by.get(k);
    if (list) list.push(r);
    else by.set(k, [r]);
  }
  return [...by.entries()]
    .sort(([a, ra], [b, rb]) => {
      if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
      return rb.length - ra.length || (a < b ? -1 : a > b ? 1 : 0);
    })
    .map(([sector, list]) => ({
      sector,
      label: sector ?? OTHER_SECTOR,
      total: list.length,
      rows: cap == null ? list : list.slice(0, cap),
    }));
}

export interface GroupedDaySlice extends DaySlice {
  /** Each time block's sector groups; a block with nobody is an empty list. */
  blocks: Record<TimeBlock, SectorGroup[]>;
  /** The matched names not shown, per sector, most first (ties alphabetical, Other last). */
  hiddenBySector: { label: string; count: number }[];
}

/**
 * A day with Group: Sector — the per-day cap replaced by `SECTOR_CAP` per
 * sector inside each time block, from the full ranked set the server sends.
 * Expanded shows every matched name, still grouped.
 */
export function sliceDayBySector(day: EarningsDay | undefined, expanded: boolean): GroupedDaySlice {
  const blocks: Record<TimeBlock, SectorGroup[]> = { bmo: [], amc: [], none: [] };
  if (!day) return { show: [], hidden: 0, matchedHidden: 0, blocks, hiddenBySector: [] };
  const cap = expanded ? undefined : SECTOR_CAP;
  for (const b of TIME_BLOCKS) blocks[b] = groupBySector(day.rows.filter((r) => blockOf(r) === b), cap);
  const shown = new Set(TIME_BLOCKS.flatMap((b) => blocks[b].flatMap((g) => g.rows)));
  // `show` in ranking order, so counts and the grid's width read as before.
  const show = day.rows.filter((r) => shown.has(r));
  const hiddenRows = day.rows.filter((r) => !shown.has(r));
  const hiddenBySector = groupBySector(hiddenRows).map((g) => ({ label: g.label, count: g.total }));
  return { show, hidden: day.total - show.length, matchedHidden: hiddenRows.length, blocks, hiddenBySector };
}

/** `Tech 41 · Financials 30 · Health 12 · Energy 9 · …` — the first few, shortened. */
export function hiddenBreakdown(list: readonly { label: string; count: number }[], named = HIDDEN_SECTORS_NAMED): string {
  const head = list.slice(0, named).map((s) => `${sectorShort(s.label)} ${s.count}`);
  if (list.length > named) head.push("…");
  return head.join(" · ");
}

// ── click a symbol → a chart layout ──────────────────────────────────────

/** A layout a symbol click may open: one that holds a chart. */
export interface JumpTarget {
  id: string;
  name: string;
}

/** The workspace's shown layouts that hold a chart, in tab order of the array, by name. */
export function chartLayoutTargets(layouts: readonly Layout[]): JumpTarget[] {
  return layouts
    .filter((l) => !l.hidden && chartSections(l.sections).length > 0)
    .map((l) => ({ id: l.id, name: l.name }));
}

/**
 * Where a symbol click goes: the saved layout when it still exists and holds
 * a chart; otherwise ask (the chooser) — or nowhere, when no layout has a chart.
 */
export function resolveJump(
  targets: readonly JumpTarget[],
  saved: string | undefined,
): { kind: "go"; target: JumpTarget } | { kind: "ask" } | { kind: "none" } {
  if (!targets.length) return { kind: "none" };
  const hit = saved ? targets.find((t) => t.id === saved) : undefined;
  return hit ? { kind: "go", target: hit } : { kind: "ask" };
}
